import { SceneService } from '../../services/SceneService';
import { AnchorResolver } from '../models/layout/AnchorResolver';
import { Scene } from '../models/layout/Scene';
import { EditorChangeType } from '../types';
import { AbstractCommand } from './AbstractCommand';

import type { VideoEditor } from '../VideoEditor';

/**
 * RemoveSceneCommand
 * Removes a scene from the timeline and validates remaining scenes
 */
export class RemoveSceneCommand extends AbstractCommand {
  private sceneId: string;
  private service: SceneService;
  private anchorResolver: AnchorResolver;

  private removedScene: Scene | null = null;

  constructor(editor: VideoEditor, sceneId: string) {
    super(editor);
    this.sceneId = sceneId;
    this.service = new SceneService();
    this.anchorResolver = new AnchorResolver();
  }

  getName(): string {
    return 'RemoveSceneCommand';
  }

  execute(): boolean {
    // Find and store the scene before removing it (for undo)
    if (this.editor.timeline.scenes.length <= 1) {
      console.error('Cannot remove the last remaining scene.');
      return false;
    }

    this.removedScene =
      this.editor.timeline.scenes.find(scene => scene.id === this.sceneId) ??
      null;

    if (!this.removedScene) {
      console.error(`Scene ${this.sceneId} not found`);
      return false;
    }

    try {
      // Sort scenes to find adjacent scenes
      const sortedScenes = [...this.editor.timeline.scenes].sort(
        (a, b) =>
          this.anchorResolver.resolve(a.anchorStart, this.editor.timeline) -
          this.anchorResolver.resolve(b.anchorStart, this.editor.timeline),
      );

      const removedIndex = sortedScenes.findIndex(
        scene => scene.id === this.sceneId,
      );

      // Store adjacent scenes for anchor adjustment
      const previousScene =
        removedIndex > 0 ? sortedScenes[removedIndex - 1] : null;
      const nextScene =
        removedIndex < sortedScenes.length - 1
          ? sortedScenes[removedIndex + 1]
          : null;

      // Remove the scene
      const removed = this.service.removeScene(
        this.editor.timeline,
        this.sceneId,
      );

      if (!removed) {
        return false;
      }

      // Adjust adjacent scene anchors
      if (previousScene) {
        // If there's a previous scene, adjust its end anchor to match removed scene's end anchor

        const updatedPreviousScene = new Scene({
          id: previousScene.id,
          anchorStart: previousScene.anchorStart,
          anchorEnd: this.removedScene.anchorEnd,
          layout: previousScene.layout.clone(),
        });

        this.service.updateScene(
          this.editor.timeline,
          previousScene.id,
          updatedPreviousScene,
        );
      } else if (nextScene) {
        // If removed scene is first, adjust next scene's start anchor to match removed scene's start anchor

        const updatedNextScene = new Scene({
          id: nextScene.id,
          anchorStart: this.removedScene.anchorStart,
          anchorEnd: nextScene.anchorEnd,
          layout: nextScene.layout.clone(),
        });

        this.service.updateScene(
          this.editor.timeline,
          nextScene.id,
          updatedNextScene,
        );
      }

      // Validate and adjust remaining scenes (close gaps)
      this.service.validateAndAdjustScenes(this.editor.timeline);
    } catch (error) {
      console.error('Failed to remove scene:', error);
      return false;
    }

    this.editor.markDirty();
    this.editor.notifyObservers(EditorChangeType.SCENE_REMOVED, {
      sceneId: this.sceneId,
    });

    return true;
  }

  undo(): boolean {
    if (!this.removedScene) {
      return false;
    }

    try {
      this.service.addScene(this.editor.timeline, this.removedScene);
    } catch (error) {
      console.error('Failed to restore scene:', error);
      return false;
    }

    this.editor.markDirty();
    this.editor.notifyObservers(EditorChangeType.SCENE_ADDED, {
      sceneId: this.removedScene.id,
      templateId: this.removedScene.layout.layoutTemplateId,
    });

    return true;
  }

  redo(): boolean {
    return this.execute();
  }

  getDescription(): string {
    return `Remove scene ${this.sceneId}`;
  }

  shouldCreateUndoRedoEntry(): boolean {
    return true;
  }
}
